import { useMemo } from "react";

import dayjs from "dayjs";
import type { Options as HighchartsOptions } from "highcharts";

import { shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";

import { parseConversionRateError } from "app/pages/website-analysis/TrafficAndEngagement/conversionRateEmptyStateUtils";
import DurationServiceSingleton from "app/services/DurationService";

import { formatValue, POP_CURRENT_COLOR, POP_PREVIOUS_COLOR } from "./constants";
import {
    renderComparePopTooltipContent,
    renderPopTooltipContent,
    renderSingleTooltipContent,
} from "./conversionRatesTooltips";
import { createParams, createPopParams, selectMetric } from "./conversionRatesUtils";
import type { ConversionsChartProps, TooltipPoint } from "./types";
import { useConversionRateData, useConversionRateDataPOP } from "./useConversionRateData";

export function useConversionRatesChart({
    params,
    meta,
    getSiteColor,
    metric,
    enabled,
    hiddenSites,
}: ConversionsChartProps) {
    const singleApiParams = useMemo(() => createParams(params), [params]);
    const popApiParams = useMemo(() => createPopParams(params), [params]);
    const singleQuery = useConversionRateData(singleApiParams, { enabled: enabled && !meta.isPOP });
    const popQuery = useConversionRateDataPOP(popApiParams, { enabled: enabled && meta.isPOP });

    const query = meta.isPOP ? popQuery : singleQuery;
    const emptyStateType = query.isError ? parseConversionRateError(query.error) : null;
    const data = useMemo(() => selectMetric(query.data ?? [], metric), [query.data, metric]);

    const popPeriodLabels = useMemo(() => {
        const [current = "", previous = ""] = [
            DurationServiceSingleton.getDurationData(params.duration, params.comparedDuration)?.forWidget,
        ].flat();

        return { current, previous };
    }, [params.duration, params.comparedDuration]);

    const { compareDateLookup, tooltipChangeLookup, compareValueLookup } = useMemo(() => {
        const dates: Record<number, string> = {};
        const changes: Record<string, number> = {};
        const compareValues: Record<string, number | null> = {};

        data.forEach((item) => {
            item.data.forEach((point) => {
                const timestamp = dayjs.utc(point.date).valueOf();
                if (point.compareDate) {
                    dates[timestamp] = point.compareDate;
                }
                changes[`${item.site}-current:${timestamp}`] = Number(point.change ?? 0);
                compareValues[`${item.site}-current:${timestamp}`] = point.compareValue ?? null;
            });
        });

        return { compareDateLookup: dates, tooltipChangeLookup: changes, compareValueLookup: compareValues };
    }, [data]);

    const siteAverages = useMemo<Record<string, number>>(
        () =>
            Object.fromEntries(
                data.filter((item) => item.average != null).map((item) => [item.site, Number(item.average)]),
            ),
        [data],
    );

    const isSinglePop = meta.isPOP && meta.isSingleMode;

    const graphSeries = useMemo(() => {
        // Compare+POP and non-POP: one line per domain with domain colors
        if (!isSinglePop) {
            return data.map((item) => ({
                name: item.site,
                id: `${item.site}-current`,
                visible: !hiddenSites?.has(item.site),
                color: getSiteColor(item.site) as StrictColor,
                data: item.data.map((point) => [dayjs.utc(String(point.date ?? "")).valueOf(), point.value ?? null]),
            }));
        }

        // Single+POP: two lines per site (current + previous)
        return data.flatMap((item) => {
            const timestamps = item.data.map((point) => dayjs.utc(String(point.date ?? "")).valueOf());

            return [
                {
                    name: popPeriodLabels.current,
                    id: `${item.site}-current`,
                    visible: true,
                    color: POP_CURRENT_COLOR,
                    data: item.data.map((point, i) => [timestamps[i], point.value ?? null]),
                },
                {
                    name: popPeriodLabels.previous,
                    id: `${item.site}-previous`,
                    visible: true,
                    color: POP_PREVIOUS_COLOR,
                    data: item.data.map((point, i) => [timestamps[i], point.compareValue ?? null]),
                },
            ];
        });
    }, [data, isSinglePop, popPeriodLabels, getSiteColor, hiddenSites]);

    const hasData = graphSeries.some((series) => series.data.some((point) => point[1] !== null));

    const popLegendItems = useMemo(() => {
        if (!isSinglePop) {
            return [];
        }

        return [
            {
                name: popPeriodLabels.current,
                color: POP_CURRENT_COLOR,
                transparentColor: colorsPalettes.blue[200],
            },
            {
                name: popPeriodLabels.previous,
                color: POP_PREVIOUS_COLOR,
                transparentColor: colorsPalettes.orange[200],
            },
        ];
    }, [isSinglePop, popPeriodLabels]);

    const graphConfig = useMemo<HighchartsOptions>(
        () => ({
            chart: {
                height: 320,
            },
            xAxis: {
                type: "datetime",
                labels: {
                    formatter({ value }) {
                        return dayjs.utc(value).format(shortMonthAndShortYear);
                    },
                },
            },
            yAxis: {
                labels: {
                    formatter({ value }) {
                        return formatValue(Number(value), metric);
                    },
                },
            },
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter() {
                    const timestamp = Number(this.x);
                    const points = (this.points || []) as TooltipPoint[];

                    if (isSinglePop) {
                        return renderPopTooltipContent(timestamp, points, {
                            metric,
                            tooltipChangeLookup,
                            compareDateLookup,
                            popPeriodLabels,
                            webSource: params.webSource ?? "",
                        });
                    }

                    if (meta.isPOP) {
                        return renderComparePopTooltipContent(timestamp, points, {
                            metric,
                            tooltipChangeLookup,
                            compareValueLookup,
                            compareDateLookup,
                        });
                    }

                    return renderSingleTooltipContent(timestamp, points, tooltipChangeLookup, metric);
                },
            },
        }),
        [
            metric,
            isSinglePop,
            meta.isPOP,
            tooltipChangeLookup,
            compareValueLookup,
            compareDateLookup,
            popPeriodLabels,
            params.webSource,
        ],
    );

    return {
        query,
        singleApiParams,
        graphSeries,
        graphConfig,
        hasData,
        popLegendItems,
        siteAverages,
        emptyStateType,
    };
}
